/**
 * Hybrid Search — cosine similarity + temporal decay + topic/people boosts.
 *
 * Scoring formula:
 *   finalScore = cosineScore * temporalDecay(createdAt) + min(metadataBoost, 0.15)
 *   (findSimilarEntries also adds peopleOverlap * 0.03)
 *
 * Temporal decay: exponential
 *   Search: 365-day half-life (year-old entry ~ 0.50)
 *   Similar: 90-day half-life (3-month-old entry ~ 0.50)
 */

import { resolveLinkedPeople } from '@/document'
import type { DocumentView } from '@/document'

import type { Entry, EntryType } from '@/document'
import type { EmbeddingRecord } from './vector-store'

// --- Types -------------------------------------------------------------------

export interface SearchFilters {
  type?: EntryType | EntryType[]
  dateAfter?: string
  dateBefore?: string
  topicId?: string
  personId?: string
}

export interface ScoredResult {
  entryId: string
  score: number
  cosineScore: number
}

// --- Constants ---------------------------------------------------------------

/** Search uses a slow decay: year-old entries retain ~50% weight. */
export const SEARCH_HALF_LIFE_DAYS = 365

/** "Similar entries" uses a faster decay: 90-day half-life. */
export const SIMILAR_HALF_LIFE_DAYS = 90

/** Maximum total metadata boost (topics + people). */
export const MAX_METADATA_BOOST = 0.15

// --- Pure scoring functions --------------------------------------------------

/** Cosine similarity on Float32Arrays. Returns 0 for zero vectors. */
export function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  let dot = 0
  let normA = 0
  let normB = 0
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i]
    normA += a[i] * a[i]
    normB += b[i] * b[i]
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB)
  return denom === 0 ? 0 : dot / denom
}

/** Exponential decay with configurable half-life. Default: 365-day (search). */
export function temporalDecay(
  createdAt: string,
  halfLifeDays = SEARCH_HALF_LIFE_DAYS,
): number {
  const days = (Date.now() - new Date(createdAt).getTime()) / 86_400_000
  return Math.exp(-(Math.LN2 / halfLifeDays) * Math.max(0, days))
}

/** Count shared topic IDs between an entry and a query context. */
export function computeTopicOverlap(
  entryTopicIds: string[],
  queryTopicIds: string[],
): number {
  const querySet = new Set(queryTopicIds)
  let count = 0
  for (const id of entryTopicIds) {
    if (querySet.has(id)) count++
  }
  return count
}

/** Pre-filter entries by metadata. Always excludes archived entries. */
export function applyFilters(
  entries: Entry[],
  filters: SearchFilters,
  view: DocumentView | null,
): Entry[] {
  return entries.filter((entry) => {
    // Always exclude archived and person entries (people are found by name, not search)
    if (entry.archivedAt) return false
    if (entry.type === 'person') return false

    // Type filter
    if (filters.type) {
      const types = Array.isArray(filters.type) ? filters.type : [filters.type]
      if (!types.includes(entry.type as EntryType)) return false
    }

    // Date range
    if (filters.dateAfter && entry.createdAt < filters.dateAfter) return false
    if (filters.dateBefore && entry.createdAt > filters.dateBefore) return false

    // Topic filter
    if (filters.topicId && !entry.topicIds.includes(filters.topicId))
      return false

    // Person filter, through the view rather than by walking links here. ADR 0012 rule 5: a
    // link whose other endpoint is archived is not a live relationship. This filter used to check
    // neither endpoint, so an entry linked only to an archived person survived it (#230).
    if (filters.personId) {
      const people = resolveLinkedPeople(entry.id, view)
      if (!people.some((p) => p.id === filters.personId)) return false
    }

    return true
  })
}

/** Score and rank candidates by cosine * decay + topic boost. */
export function rankResults(
  candidates: { entryId: string; cosineScore: number; entry: Entry }[],
  queryTopicIds: string[],
): ScoredResult[] {
  const scored = candidates.map((c) => {
    const decay = temporalDecay(c.entry.createdAt, SEARCH_HALF_LIFE_DAYS)
    const topicBoost =
      computeTopicOverlap(c.entry.topicIds, queryTopicIds) * 0.05
    const finalScore =
      c.cosineScore * decay + Math.min(topicBoost, MAX_METADATA_BOOST)
    return { entryId: c.entryId, score: finalScore, cosineScore: c.cosineScore }
  })
  scored.sort((a, b) => b.score - a.score)
  return scored
}

// --- Main functions ----------------------------------------------------------

/** Full hybrid search: filter, embed query, score, rank. */
export async function hybridSearch(
  query: string,
  doc: DocumentView,
  embedFn: (texts: string[]) => Promise<number[][]>,
  allEmbeddings: EmbeddingRecord[],
  filters?: SearchFilters,
  topK = 10,
): Promise<ScoredResult[]> {
  // 1. Gather non-archived entries, apply filters
  const allEntries = Object.values(doc.entries)
  const filtered = applyFilters(allEntries, filters ?? {}, doc)
  const candidateIds = new Set(filtered.map((e) => e.id))

  // 2. Filter embeddings to candidates only
  const candidateEmbeddings = allEmbeddings.filter((r) =>
    candidateIds.has(r.entryId),
  )
  if (candidateEmbeddings.length === 0) return []

  // 3. Embed query with EmbeddingGemma search prefix
  // See: https://huggingface.co/google/embeddinggemma-300m
  const [queryVec] = await embedFn([`task: search result | query: ${query}`])
  const queryEmb = new Float32Array(queryVec)

  // 4. Score each candidate
  const entryMap = new Map(filtered.map((e) => [e.id, e]))
  const candidates = candidateEmbeddings
    .map((rec) => {
      const entry = entryMap.get(rec.entryId)
      if (!entry) return null
      const cosineScore = cosineSimilarity(
        queryEmb,
        new Float32Array(rec.embedding),
      )
      return { entryId: rec.entryId, cosineScore, entry }
    })
    .filter((c): c is NonNullable<typeof c> => c !== null)

  // 5. Rank — extract topic IDs from query context (none by default)
  return rankResults(candidates, []).slice(0, topK)
}

/**
 * Find entries similar to a given entry (synchronous — uses stored embeddings).
 * Boosts by shared topics (+0.05/topic) and shared people (+0.03/person).
 */
export function findSimilarEntries(
  entryId: string,
  doc: DocumentView,
  allEmbeddings: EmbeddingRecord[],
  topK = 10,
): ScoredResult[] {
  // Find source embedding
  const sourceRec = allEmbeddings.find((r) => r.entryId === entryId)
  if (!sourceRec) return []
  const sourceEmb = new Float32Array(sourceRec.embedding)
  const sourceEntry = doc.entries[entryId]
  if (!sourceEntry) return []

  // The source entry's people, through the view. This walk had no archived check at all before
  // #230, which is what made it disagree with `entry-text.ts` about the same question.
  const sourcePersonIds = new Set(
    resolveLinkedPeople(entryId, doc).map((person) => person.id),
  )

  // Score all other non-archived entries that have embeddings
  const results: ScoredResult[] = []
  for (const rec of allEmbeddings) {
    if (rec.entryId === entryId) continue
    const entry = doc.entries[rec.entryId]
    if (!entry || entry.archivedAt) continue

    const cosineScore = cosineSimilarity(
      sourceEmb,
      new Float32Array(rec.embedding),
    )
    const topicBoost =
      computeTopicOverlap(entry.topicIds, sourceEntry.topicIds) * 0.05

    // Same rule on the candidate's side.
    const peopleOverlap = resolveLinkedPeople(rec.entryId, doc).filter(
      (person) => sourcePersonIds.has(person.id),
    ).length
    const peopleBoost = peopleOverlap * 0.03

    const metadataBoost = Math.min(topicBoost + peopleBoost, MAX_METADATA_BOOST)
    const decay = temporalDecay(entry.createdAt, SIMILAR_HALF_LIFE_DAYS)
    const score = cosineScore * decay + metadataBoost
    results.push({ entryId: rec.entryId, score, cosineScore })
  }

  results.sort((a, b) => b.score - a.score)
  return results.slice(0, topK)
}
